import { axisLeft } from "d3-axis";
import { scaleBand } from "d3-scale";
import { select } from "d3-selection";
import { getElementOrAncestorWithClass } from "@/helpers/utility/DOM";
import type { NumberValue, ScaleLinear } from "d3-scale";
import type { Selection, BaseType } from "d3-selection";
import type { Transition } from "d3-transition";
import type { EntryValue, ColumnEntry } from "./ChartData";
import type { UnfilledSymbolName } from "@core/ui/svg-icons/symbols/SymbolName";
import type { ChartSettings } from "@editor/items/charts/ChartItem";

/**
 * Draw rects with the value ranges of the elements:
 */
export function drawColorScale(
  g: Selection<SVGGElement, unknown, null, undefined> | null,
  scale: ScaleLinear<number, number, never> | undefined,
  settings: {
    scaleColorsVisible?: boolean;
    scaleColors?: string[];
    id?: string;
    height?: number;
    animationDuration?: number;
  } = {}
) {
  if (!g) return;
  if (!scale) return;

  let colorScale = g.select<SVGGElement>(".chart-colorscale");

  // Remove the scale if it's not needed.
  if (!settings.scaleColorsVisible || isNaN(scale(0))) {
    colorScale
      .selectAll<SVGRectElement, string>(".chart-colorscale__rect")
      .remove();

    return;
  }

  // create the color scale container
  if (colorScale.empty()) {
    colorScale = g.append("g").attr("class", "chart-colorscale");
  }

  if (colorScale.select(`#clip-path-${settings.id}`).empty()) {
    colorScale
      .append("clipPath")
      .attr("id", `clip-path-${settings.id}`)
      .append("rect")
      .attr("x", -12)
      .attr("y", 0)
      .attr("width", 12);
  }

  colorScale
    .select(`#clip-path-${settings.id}`)
    .select("rect")
    .transition()
    .duration(settings.animationDuration || 0)
    .attr("height", Math.max(settings.height ?? 0, 0));

  // Color ranges do only include the color, as string.
  const colors = settings.scaleColors ?? [];
  const length = colors.length;

  if (length <= 0) return colorScale;

  const RECT_WIDTH = 12;
  const RECT_HEIGHT = Math.abs(scale(0) - scale(100 / length));

  if (isNaN(RECT_HEIGHT)) return colorScale;

  // Loop through the possible y value ranges,
  // adding colored rects on the Y axis.
  colorScale
    .selectAll<SVGRectElement, string>(".chart-colorscale__rect")
    .data(colors)
    .join(
      (enter) =>
        enter
          .append("rect")
          .attr("x", 0)
          .attr("clip-path", `url(#clip-path-${settings.id})`)
          .attr("class", "chart-colorscale__rect")
          .attr("fill", (d) => d)
          .attr("y", (_d, i) => scale((100 / length) * i) - RECT_HEIGHT)
          .attr("height", RECT_HEIGHT),
      (update) => update,
      (exit) => exit.remove()
    )
    .attr("fill", (d) => d)
    .transition()
    .duration(settings.animationDuration || 0)
    .attr("width", RECT_WIDTH)
    .attr("height", RECT_HEIGHT)
    .attr("y", (_d, i) => scale((100 / length) * i) - RECT_HEIGHT)
    .attr("x", -12);

  return colorScale;
}

export function drawVerticalYAxis<P extends BaseType>(
  units: string[],
  g: Selection<SVGGElement, unknown, P, undefined>,
  yScales: { [unit: string]: ScaleLinear<number, number, never> | undefined },
  settings: Partial<ChartSettings> & {
    unitColors?: { [unit: string]: string | null | undefined };
    width: number;
    animationDuration: number;
    xOffset?: number;
    yUnitWidths?: Record<string, number>;
    fontSize?: number;
    yAxisTitleVisible?: boolean;
  }
) {
  const fontSize = settings.fontSize || 12;
  return g
    .selectAll<SVGGElement, string>(".chart__y-ticks")
    .data(units, (unit) => unit)
    .join("g")
    .attr(
      "class",
      (unit) =>
        `chart__y-ticks -unit-${unit.replaceAll(/\s"'<>/g, "")}`
    )
    .each(function (unit, i) {
      const scale = yScales[unit];
      if (!scale) return;

      const group = select(this);

      const barX = units.slice(i + 1).reduce((sum, u) => {
        const result = settings.yUnitWidths?.[u] ?? fontSize * 4;
        return result + sum;
      }, 0);
      const gapsWidth = settings.yAxisTitleVisible ? fontSize * 2 : fontSize * 0.8;
      const gapOffset = Math.max(0, (units.length - 1 - i) * gapsWidth);

      group.classed(
        "-color-scale-visible",
        unit == "%" && settings.scaleColorsVisible == true
      );

      if (settings.unitColors?.[unit]) {
        group.style("--color-tick-text", settings.unitColors?.[unit] || "");
        group.style("--color-domain", settings.unitColors?.[unit] || "");
      } else {
        group.style("--color-tick-text", null);
        group.style("--color-domain", null);
      }

      // Create the axis format:
      const drawAxis = axisLeft(scale)
        .tickFormat(settings.yAxisLabelsVisible ? (number) => `${number}` : () => "")
        .ticks(5)
        .tickPadding(10)
        .tickSizeInner(
          settings.ticksVisible ? -settings.width - barX - gapOffset : 0
        )
        .tickSizeOuter(0);

      // create the axis
      const axisTransition = group
        .transition()
        .duration(settings.animationDuration || 0)
        .attr("transform", () => {
          return `translate(${-barX - gapOffset}, 0)`;
        })
        .call(drawAxis);

      // style the lines
      group
        .selectAll(".tick line")
        .attr("class", "-full-width")
        .attr("x1", "-5");
      group.selectAll(".tick").classed("is-zero", (d) => {
        return d == 0;
      });

      axisTransition.selectAll(".tick line").attr("stroke", null);

      const colorScaleOffset =
        unit == "%" && settings.scaleColorsVisible ? -9 : 0;
      
      if (!settings.yAxisLabelsVisible && !settings.yAxisTitleVisible) {
        axisTransition.selectAll(".domain").attr("stroke-width", 0);
      } else {
        axisTransition.selectAll(".domain").attr("stroke-width", 1);
      }

      // move y axis text to the left
      axisTransition
        .selectAll(".tick text")
        .attr("transform", `translate(${colorScaleOffset}, 0)`)
        .attr("fill", null)
        .style("font-size", `${fontSize}px`);
    });
}

export function scaleHeight(
  scale: ((value: NumberValue) => number) | undefined,
  height: number
) {
  if (!scale) return 0;
  const hh = scale(Math.max(height, 0));
  const zero = scale(0);
  return Math.abs(hh - zero);
}

/**
 * Wraps the contents of a text node into multiple lines
 * @param text
 * @param width
 */
export function wrapText<V, P extends BaseType, D>(
  text:
    | Selection<SVGTextElement | BaseType, V, P, D>
    | Transition<SVGTextElement | BaseType, V, P, D>,
  getWidth: number | ((d: V, i: number) => number),
  measureTextWidth: (text: string, size: number) => number,
  fontSize = 16
) {
  return text.each(function (d, i, groups) {
    const lineHeight = 1.1; // ems;

    const textNode = select(this);

    const width = typeof getWidth == "number" ? getWidth : getWidth(d, i);

    let x: string | number = textNode.attr("x");
    let y: string | number = textNode.attr("y");

    let xFrom: number | string = x;
    let yFrom: number | string = y;

    const textTransition = text.filter((d, _i) => i == _i);
    if ("attrTween" in textTransition) {
      const xTween = textTransition.attrTween("x");
      const yTween = textTransition.attrTween("y");
      if (xTween) {
        const getX = xTween.call(this, d, i, groups);
        if (getX) {
          x = getX.call(this, 1);
          xFrom = getX.call(this, 0);
        }
      }
      if (yTween) {
        const getY = yTween.call(this, d, i, groups);
        if (getY) {
          y = getY.call(this, 1);
          yFrom = getY.call(this, 0);
        }
      }
    }

    const words = textNode.text().split(/\s+/).reverse();

    const dy = parseFloat(textNode.attr("dy") || "0");

    let lineCount = 0;

    let word: string | undefined;
    let line: string[] = [],
      lineNumber = 0,
      tspan = textNode
        .text(null)
        .append("tspan")
        .attr("dy", dy + "em");

    word = words.pop();
    while (word != null) {
      line.push(word);
      const text = line.join(" ");
      if (measureTextWidth(text, fontSize) > width) {
        line.pop();
        tspan.text(line.join(" "));
        line = [word];
        lineCount++;
        tspan = textNode
          .append("tspan")
          .text(word)
          .attr("dy", ++lineNumber * lineHeight + dy + "em");
      } else {
        tspan.text(text);
      }
      word = words.pop();
    }

    let offsetY = 0;
    if (textNode.attr("text-anchor-vertical") == "end") {
      offsetY = lineCount * lineHeight * fontSize;
    }

    textNode
      .selectAll("tspan")
      .attr("x", xFrom)
      .attr("y", parseInt(yFrom) - offsetY);

    // This separation is only there to appease typescript.
    // Keep the content of both paths the same.
    if ("attrTween" in textTransition) {
      textTransition
        .selectAll<SVGTSpanElement, V>("tspan")
        .attr("x", x)
        .attr("y", parseInt(y) - offsetY);
    } else {
      textTransition
        .selectAll<SVGTSpanElement, V>("tspan")
        .attr("x", x)
        .attr("y", parseInt(y) - offsetY);
    }
  });
}

/**
 * Used in charts that have rounds as x axis.
 * Adds symbols for actions, incidents and scheduled effects
 * to every column for every played round of every simulation.
 */
export function addRoundActionSymbols(
  container: Selection<SVGGElement, unknown, null, undefined>,
  columnData: Map<string, ColumnEntry> | undefined,
  onClick: (
    event: PointerEvent,
    position: { x: number; y: number },
    item: EntryValue
  ) => void,
  { columnWidth = 0, animationDuration = 200, height = 30 } = {}
) {
  // Add round action symbols
  const roundActionsBase = container
    .select(".gx")
    .selectAll<SVGElement, string>(".tick")
    .selectAll(".chart-roundactions")
    .data(
      (columnId) => {
        const columnEntry = columnData?.get(columnId) || null;
        if (!columnEntry) return [];
        return columnEntry.values || [];
      },
      (d) => (d as EntryValue).key
    );

  const simulations = [...(columnData?.values() || [])].reduce(
    (all, columnEntry) => {
      const values = columnEntry.values || [];
      if (values.length == 0) return all;
      for (const value of values) {
        if (
          value.simulationId != null &&
          !all.find((sim) => sim.id == value.simulationId)
        ) {
          all.push({ id: value.simulationId, symbol: value.symbol });
        }
      }
      return all;
    },
    [] as { id: number; symbol?: UnfilledSymbolName }[]
  );

  const baseY = -height + 13;

  const scale = scaleBand<number>()
    .domain(simulations.map((simulation) => simulation.id))
    .range([baseY, baseY + simulations.length * 16]);

  const roundActions = roundActionsBase.join(
    (enter) => {
      const g = enter
        .append("g")
        .attr("class", "chart-roundactions")
        .style("opacity", 0)
        .attr("transform", (d) => {
          if (simulations.length == 0) {
            return `translate(0, ${baseY})`;
          }

          const simId = d.simulationId;
          const y = scale(simId || 0) || 0;
          return `translate(0, ${y})`;
        })
        .on("click", (event: PointerEvent, columnEntry) => {
          if (columnEntry == null) return;

          if (event.target == null) return;

          const tickElement = getElementOrAncestorWithClass(
            event.target as HTMLElement,
            "tick"
          );

          if (!tickElement) return;

          const rect = tickElement.getBoundingClientRect();
          const y = rect.top - 10;

          onClick(event, { x: rect.left + rect.width / 2, y }, columnEntry);
        })
        .on("mouseenter", function () {
          select(this).classed("-hovered", true);
        })
        .on("mouseleave", function () {
          select(this).classed("-hovered", false);
        });

      g.transition().duration(animationDuration).style("opacity", 1);

      g.append("rect").attr(
        "class",
        "chart-roundactions__background"
      );

      const total = g
        .append("g")
        .attr("class", "chart-roundactions__total");
      total.append("text");

      const incidents = g
        .append("g")
        .attr(
          "class",
          "chart-roundactions__incidents chart-roundactions__entry"
        );
      incidents.append("text").attr("text-anchor", "end");
      incidents
        .append("use")
        .attr("width", 110)
        .attr("height", 154)
        .attr("x", 30)
        .attr("y", -140)
        .attr("class", "chart-roundactions__symbol")
        .attr("href", "#svg-icons-incident");

      const actions = g
        .append("g")
        .attr(
          "class",
          "chart-roundactions__actions chart-roundactions__entry"
        );
      actions.append("text").attr("text-anchor", "end");
      actions
        .append("use")
        .attr("width", 110)
        .attr("height", 154)
        .attr("x", 30)
        .attr("y", -140)
        .attr("class", "chart-roundactions__symbol")
        .attr("href", "#svg-icons-action");

      const scheduledEffects = g
        .append("g")
        .attr(
          "class",
          "chart-roundactions__effects chart-roundactions__entry"
        );
      scheduledEffects.append("text").attr("text-anchor", "end");
      scheduledEffects
        .append("use")
        .attr("width", 110)
        .attr("height", 154)
        .attr("x", 30)
        .attr("y", -140)
        .attr("class", "chart-roundactions__symbol")
        .attr("href", "#svg-icons-impulse");

      return g;
    },
    (update) => {
      return update
        .transition()
        .duration(animationDuration)
        .style("opacity", 1)
        .attr("transform", (d) => {
          if (simulations.length == 0) return `translate(0, ${baseY})`;

          const simId = d.simulationId;
          const y = scale(simId || 0) || 0;
          return `translate(0, ${y})`;
        });
    },
    (exit) => exit.remove()
  );

  roundActions.each(function (d) {
    const roundAction = select(this);
    const values = d.subValues || [];
    const incidentActivations = values.filter(
      (v) => v.type == "incidentActivation"
    );
    const actionActivations = values.filter(
      (v) => v.type == "actionActivation"
    );
    const scheduledEffectActivations = values.filter(
      (v) => v.type == "scheduledEffectActivation"
    );
    const incidentsCount = incidentActivations.length || 0;
    const actionsCount = actionActivations.length || 0;
    const effectsCount = scheduledEffectActivations.length || 0;

    const incidentsTextWidth =
      incidentsCount > 0 ? `${incidentsCount}`.length * 5 : 0;
    const actionsTextWidth =
      actionsCount > 0 ? `${actionsCount}`.length * 5 : 0;
    const effectsTextWidth =
      effectsCount > 0 ? `${effectsCount}`.length * 5 : 0;

    let activeItems = 0;
    if (incidentsCount > 0) activeItems++;
    if (actionsCount > 0) activeItems++;
    if (effectsCount > 0) activeItems++;

    const SYMBOL_GAP = 2;
    const SYMBOL_WIDTH = 10;
    const SYMBOL_OFFSET = 2;

    const totalWidth =
      incidentsTextWidth +
      actionsTextWidth +
      effectsTextWidth +
      SYMBOL_WIDTH * activeItems +
      15;

    const smallVersionActive = totalWidth >= columnWidth;

    const totalCount = incidentsCount + actionsCount + effectsCount;

    roundAction
      .select(".chart-roundactions__background")
      .attr("y", -12)
      .attr("x", Math.min(-columnWidth / 2, 0) + 1)
      .attr("width", Math.max(columnWidth - 2, 0))
      .attr("height", 14);

    roundAction
      .select(".chart-roundactions__total")
      .style("opacity", (smallVersionActive && totalCount != 0) ? 1 : 0)
      .select("text")
      .text(totalCount);

    roundAction
      .select(".chart-roundactions__incidents")
      .style("opacity", smallVersionActive ? 0 : incidentsCount == 0 ? 0 : 1)
      .attr("transform", () => {
        if (d == null) return `translate(${SYMBOL_OFFSET},0)`;

        const textWidth = incidentsTextWidth;
        const startOffset = SYMBOL_OFFSET - textWidth / 2;

        if (actionsCount + effectsCount == 0) {
          return `translate(${startOffset},0)`;
        }

        const width = textWidth + SYMBOL_WIDTH;

        if (actionsCount == 0 || effectsCount == 0) {
          // move a bit to the left
          return `translate(${startOffset - width / 2 - SYMBOL_GAP}, 0)`;
        }

        // move more to the left
        return `translate(${startOffset - width - SYMBOL_GAP}, 0)`;
      })
      .call((g) => g.select("text").text(`${incidentsCount || ""}`));

    roundAction
      .select(".chart-roundactions__actions")
      .style("opacity", smallVersionActive ? 0 : actionsCount == 0 ? 0 : 1)
      .attr("transform", () => {
        if (d == null) return `translate(${SYMBOL_OFFSET},0)`;

        const textWidth = actionsTextWidth;

        const startOffset = SYMBOL_OFFSET - textWidth / 2;

        if (incidentsCount + effectsCount == 0) {
          return `translate(${startOffset},0)`;
        }

        const width = textWidth + SYMBOL_WIDTH;

        if (incidentsCount == 0) {
          // move to the left if incidents are missing
          return `translate(${startOffset - width / 2 - SYMBOL_GAP}, 0)`;
        } else if (effectsCount == 0) {
          // move to the right if effects are missing
          return `translate(${startOffset + width / 2 + SYMBOL_GAP}, 0)`;
        }

        // stay in the middle if all three exist.
        return `translate(${startOffset}, 0)`;
      })
      .call((g) => g.select("text").text(`${actionsCount || ""}`));

    roundAction
      .select(".chart-roundactions__effects")
      .style("opacity", smallVersionActive ? 0 : effectsCount == 0 ? 0 : 1)
      .attr("transform", () => {
        if (d == null) return `translate(${SYMBOL_OFFSET},0)`;

        const textWidth = effectsTextWidth;
        const startOffset = SYMBOL_OFFSET - textWidth / 2;

        if (incidentsCount + actionsCount == 0) {
          return `translate(${startOffset},0)`;
        }

        const width = textWidth + SYMBOL_WIDTH;

        if (incidentsCount == 0 || actionsCount == 0) {
          // move a bit to the right
          return `translate(${startOffset + width / 2 + SYMBOL_GAP}, 0)`;
        }

        // move more to the right
        return `translate(${startOffset + width + SYMBOL_GAP}, 0)`;
      })
      .call((g) => g.select("text").text(`${effectsCount || ""}`));
  });

  container
    .select(".gx")
    .selectAll(".roundactions-simsymbol")
    .data(
      simulations.length >= 2 ? simulations : [],
      (sim) => (sim as { id: number }).id
    )
    .join(
      (enter) => {
        const g = enter
          .append("g")
          .attr("class", "roundactions-simsymbol")
          .attr("transform", `translate(-5, ${baseY - 1})`)
          .style("opacity", 0);

        g.append("use")
          .attr("width", 70)
          .attr("height", 70)
          .attr("x", -35)
          .attr("y", -35)
          .attr("class", "roundactions-simsymbol__symbol")
          .attr("fill", "var(--color-text)")
          .attr("href", (d) => `#svg-symbols-${d.symbol}-filled`)
          .attr("transform", "scale(0.15)");

        return g;
      },
      (update) => {
        update
          .select(".roundactions-simsymbol__symbol")
          .attr("href", (d) => `#svg-symbols-${d.symbol}-filled`);
        return update;
      },
      (exit) => exit.remove()
    )
    .transition()
    .duration(animationDuration)
    .style("opacity", 1)
    .attr("transform", (d) => {
      const y = scale(d.id) || 0;
      return `translate(-2, ${y - 5})`;
    });
}

export function drawEvaluationTitle(
  svgElement: SVGElement,
  width: number,
  titleParts: {
    text: string;
    style?: string;
  }[],
  fontSize = 16
) {
  const svg = select(svgElement);

  const HEADER_HEIGHT = 2.875 * fontSize;

  const header = selectOrCreateChild(svg, "g.chart__header");
  header.attr("font-size", fontSize);

  // background color
  selectOrCreateChild(header, "rect.chart__header-bg")
    .attr("width", width)
    .attr("height", HEADER_HEIGHT);

  // title
  selectOrCreateChild(header, "text.chart__title")
    .attr("y", fontSize * 1.8)
    .attr("x", fontSize * 1.25)
    .attr("fill", "var(--color-text)")
    .selectAll("tspan")
    .data(titleParts)
    .join("tspan")
    .text((d) => d.text)
    .attr("style", (d) => d.style || "");

  const ownerSVG = select(svgElement.ownerSVGElement || svgElement);
  const defs = selectOrCreateChild(ownerSVG, "defs");

  const id = (ownerSVG.attr("id") || "") + "-chart__header-fade";

  // create a gradient to fade in the header
  const gradient = selectOrCreateChild(
    defs,
    "linearGradient.chart__header-fade"
  ).attr("id", id);

  selectOrCreateChild(gradient, "stop.chart__header-fade-start")
    .attr("offset", "0%")
    .attr("stop-color", "var(--color-background)")
    .attr("stop-opacity", "0");
  selectOrCreateChild(gradient, "stop.chart__header-fade-end")
    .attr("offset", "20%")
    .attr("stop-opacity", "1")
    .attr("stop-color", "var(--color-background)");

  selectOrCreateChild(header, "rect.chart__end")
    .attr("y", 0)
    .attr("x", width - 100)
    .attr("width", 100)
    .attr("height", HEADER_HEIGHT)
    .attr("fill", `url(#${id})`);

  return header;
}

/**
 *
 * @param selection A d3 selection
 * @param selector MUST include the tag name, e.g. "g.chart__header"
 */
export function selectOrCreateChild<
  GElement extends BaseType,
  Datum = unknown,
  PElement extends BaseType = BaseType,
  PDatum = unknown
>(selection: Selection<GElement, Datum, PElement, PDatum>, selector: string) {
  const child = selection.selectChild(selector);
  if (child.empty()) {
    const selectorParts = selector.split(".");
    const classes = selectorParts.slice(1).filter((d) => d.length > 0);
    const tag = selectorParts[0];
    return selection.append(tag).attr("class", classes.join(" "));
  }
  return child;
}
